import {
  Component,
  Input,
  OnChanges,
  SimpleChanges,
} from '@angular/core';

interface DigitState {
  value: number;
  offset: number;
  snapping: boolean;
}

const ROLL_DURATION_MS = 1000;
const SNAP_BUFFER_MS = 40;

@Component({
  selector: 'app-rolling-counter',
  standalone: false,
  templateUrl: './rolling-counter.component.html',
  styleUrls: ['./rolling-counter.component.scss'],
})
export class RollingCounterComponent implements OnChanges {
  @Input() value: number | string = 0;
  @Input() decimals: number = 0;
  @Input() suffix: string = '';
  @Input() minDigits: number = 1;

  digits: DigitState[] = [];
  decimalDigits: DigitState[] = [];
  hasFraction: boolean = false;
  isNegative: boolean = false;

  private displayedValue: number = 0;
  private snapTimer: any = null;

  ngOnChanges(changes: SimpleChanges): void {
    if (!('value' in changes)) return;
    const next = this.toNumber(this.value);
    this.applyValue(next);
  }

  private toNumber(raw: number | string): number {
    if (typeof raw === 'number') return isFinite(raw) ? raw : 0;
    const parsed = parseFloat(raw);
    return isFinite(parsed) ? parsed : 0;
  }

  private applyValue(next: number): void {
    this.isNegative = next < 0;
    const absNext = Math.abs(next);
    const absPrev = Math.abs(this.displayedValue);

    const factor = Math.pow(10, this.decimals);
    const scaledNext = Math.round(absNext * factor);
    const scaledPrev = Math.round(absPrev * factor);

    const intPartNext = this.decimals > 0 ? Math.floor(scaledNext / factor) : scaledNext;
    const intPartPrev = this.decimals > 0 ? Math.floor(scaledPrev / factor) : scaledPrev;

    const intStrNext = String(intPartNext).padStart(this.minDigits, '0');
    const intStrPrev = String(intPartPrev).padStart(intStrNext.length, '0');

    this.digits = this.advanceDigits(this.digits, intStrPrev, intStrNext);

    if (this.decimals > 0) {
      this.hasFraction = true;
      const fracNext = String(scaledNext % factor).padStart(this.decimals, '0');
      const fracPrev = String(scaledPrev % factor).padStart(this.decimals, '0');
      this.decimalDigits = this.advanceDigits(this.decimalDigits, fracPrev, fracNext);
    } else {
      this.hasFraction = false;
      this.decimalDigits = [];
    }

    this.displayedValue = next;
    this.scheduleSnap();
  }

  private advanceDigits(
    current: DigitState[],
    prevStr: string,
    nextStr: string
  ): DigitState[] {
    const len = nextStr.length;
    const lengthChanged = current.length !== len || prevStr.length !== len;

    return Array.from({ length: len }, (_, i) => {
      const nextVal = this.digitChar(nextStr[i]);

      if (lengthChanged || !current[i]) {
        return { value: nextVal, offset: nextVal, snapping: false };
      }

      const cur = current[i];
      const baseOffset = cur.offset % 10;
      const forwardDelta = (nextVal - baseOffset + 10) % 10;
      // Roll forward from the normalised base (0-9), not the raw accumulated
      // offset. The reel only has 20 cells, so an offset that keeps growing
      // across rapid updates (before the snap timer fires) eventually scrolls
      // past the last cell and the digit renders blank. baseOffset + delta is
      // capped at 18, always landing on a real cell.
      const newOffset = baseOffset + forwardDelta;
      // If the previous roll hasn't been snapped back yet (offset still in the
      // second cycle), resetting to the lower base would animate the reel
      // backwards. Teleport without a transition instead so it stays forward.
      const interrupted = cur.offset > 9;
      return {
        value: nextVal,
        offset: newOffset,
        snapping: interrupted,
      };
    });
  }

  private digitChar(char: string): number {
    return char >= '0' && char <= '9' ? Number(char) : 0;
  }

  private scheduleSnap(): void {
    const needsSnap =
      this.digits.some((d) => d.offset > 9) ||
      this.decimalDigits.some((d) => d.offset > 9);

    if (!needsSnap) return;

    if (this.snapTimer) {
      clearTimeout(this.snapTimer);
    }

    this.snapTimer = setTimeout(() => {
      this.snapTimer = null;
      this.digits = this.digits.map((d) =>
        d.offset > 9 ? { value: d.value, offset: d.value, snapping: true } : d
      );
      this.decimalDigits = this.decimalDigits.map((d) =>
        d.offset > 9 ? { value: d.value, offset: d.value, snapping: true } : d
      );

      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.digits = this.digits.map((d) =>
            d.snapping ? { ...d, snapping: false } : d
          );
          this.decimalDigits = this.decimalDigits.map((d) =>
            d.snapping ? { ...d, snapping: false } : d
          );
            });
      });
    }, ROLL_DURATION_MS + SNAP_BUFFER_MS);
  }

  trackDigit(index: number): number {
    return index;
  }

  digitOffset(d: DigitState): string {
    return `translateY(-${d.offset}em)`;
  }
}
